import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import useLocalizedCopy from '../hooks/useLocalizedCopy';
import useReceivedEmailsCount from '../hooks/useReceivedEmailsCount';
import useEmailFollowUpEnabled from '../hooks/useEmailFollowUpEnabled';

const MobileMenuDrawer: React.FC = () => {
  const { copy } = useLocalizedCopy();
  const { currentUser, viewingAllAccounts, logout } = useAuth();
  const { unreadCount } = useReceivedEmailsCount();
  const { emailFollowUpEnabled } = useEmailFollowUpEnabled();

  const closeDrawer = () => {
    const drawer = document.getElementById('mobile-menu-drawer');
    if (drawer) {
      drawer.classList.remove('show');
    }
  };

  const handleLogout = () => {
    closeDrawer();
    logout();
  };

  const MenuSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-4">
        {title}
      </h3>
      <div className="space-y-1">
        {children}
      </div>
    </div>
  );

  const MenuItem: React.FC<{ to: string; icon: string; label: string; onClick?: () => void }> = ({ 
    to, 
    icon, 
    label,
    onClick 
  }) => (
    <Link
      to={to}
      className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-coal-500 transition-colors"
      onClick={() => {
        if (onClick) onClick();
        closeDrawer();
      }}
    >
      <i className={`ki-filled ${icon} text-lg text-gray-500 dark:text-gray-400`}></i>
      <span className="text-sm font-medium">{label}</span>
    </Link>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        id="mobile-menu-backdrop"
        className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 opacity-0 pointer-events-none transition-opacity"
        onClick={closeDrawer}
      />

      {/* Drawer */}
      <div
        id="mobile-menu-drawer"
        className="lg:hidden fixed right-0 top-0 bottom-0 w-80 bg-white dark:bg-coal-600 z-50 transform translate-x-full transition-transform overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-coal-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Menu</h2>
            {currentUser && (
              <p className="text-sm text-gray-600 dark:text-gray-400">{currentUser.email}</p>
            )}
          </div>
          <button
            onClick={closeDrawer}
            className="p-2 hover:bg-gray-100 dark:hover:bg-coal-500 rounded-lg transition-colors"
          >
            <i className="ki-filled ki-cross text-gray-500 dark:text-gray-400"></i>
          </button>
        </div>

        {/* Menu Content */}
        <div className="py-4">
          {/* Books Section */}
          <MenuSection title="Books">
            <MenuItem to="/books" icon="ki-book" label={copy.Books.viewBooks} />
            {!viewingAllAccounts && (
              <MenuItem to="/create-book" icon="ki-plus" label={copy.Books.createBook} />
            )}
          </MenuSection>

          {/* iPads Section */}
          <MenuSection title="iPads">
            <MenuItem to="/ipads" icon="ki-tablet" label={copy.IPads.list.title} />
            {!viewingAllAccounts && (
              <MenuItem to="/add-ipad" icon="ki-plus" label={copy.IPads.add.title} />
            )}
          </MenuSection>

          {/* Marketing Section */}
          <MenuSection title="Marketing">
            <MenuItem to="/follow-up-messages/review" icon="ki-sms" label="Follow-up Messages" />
            {(viewingAllAccounts || emailFollowUpEnabled) && (
              <MenuItem 
                to="/emails/received/all" 
                icon="ki-message-text-2" 
                label={`Received Emails ${unreadCount > 0 ? `(${unreadCount > 99 ? '99+' : unreadCount})` : ''}`}
              />
            )}
          </MenuSection>

          {/* Account Management */}
          {!viewingAllAccounts && (
            <MenuSection title="Account">
              <MenuItem to="/manage-account/details" icon="ki-briefcase" label={copy.Account.details.title} />
            </MenuSection>
          )}

          {/* Management Section (for super users) */}
          {(currentUser?.is_super_super_user || currentUser?.is_agency_admin) && (
            <MenuSection title="Management">
              <MenuItem to="/management/accounts/list" icon="ki-briefcase" label={copy.Management.viewAccounts} />
              <MenuItem to="/management/add-account" icon="ki-plus" label={copy.Management.addAccount} />
              <MenuItem to="/management/users" icon="ki-users" label={copy.Management.viewUsers} />
              <MenuItem to="/management/add-user" icon="ki-plus" label={copy.Account.addUser.title} />
            </MenuSection>
          )}

          {/* Admin Section (for super super users) */}
          {currentUser?.is_super_super_user && (
            <MenuSection title="Admin">
              <MenuItem to="/admin/agencies" icon="ki-briefcase" label={copy.Admin.viewAgencies} />
              <MenuItem to="/admin/add-agency" icon="ki-plus" label={copy.Admin.addAgency} />
              <MenuItem to="/admin/manage-crops" icon="ki-frame" label={copy.Admin.manageCrops} />
              <MenuItem to="/admin/generated-pdfs" icon="ki-size" label={copy.Admin.generatedPDFs} />
              {currentUser.email === 'chris@cohoat.tech' && (
                <>
                  <MenuItem to="/admin/system-settings" icon="ki-setting-2" label={copy.Admin.systemSettings} />
                  <MenuItem to="/admin/transcription-logs" icon="ki-notepad" label="Transcription Logs" />
                </>
              )}
            </MenuSection>
          )}

          {/* User Section */}
          <MenuSection title="User">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors w-full text-left"
            >
              <i className="ki-filled ki-exit text-lg"></i>
              <span className="text-sm font-medium">Logout</span>
            </button>
          </MenuSection>
        </div>
      </div>

    </>
  );
};

export default MobileMenuDrawer;